import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import { createStrategyBatch, getStrategies } from "../../api/strategyProfiling";
import { parseCreateBatchResponse } from "./batchManagementUtils";
import styles from "./BatchManagementTab.module.css";

const getErrorMessage = (err) => err?.response?.data?.detail || err.message || "An error occurred";

const getStrategyId = (strategy) => strategy?.id || strategy?.strategy_id || "";

const STRATEGY_SELECT_MENU_PROPS = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  MenuListProps: {
    dense: true,
    className: styles["create-batch-select-list"],
  },
  slotProps: {
    paper: {
      className: styles["create-batch-select-menu"],
      sx: { maxHeight: 240 },
    },
  },
};

function CreateBatchIdModal({ open, onClose, onSuccess }) {
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState("");
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successResult, setSuccessResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    setIsLoadingStrategies(true);
    setError("");
    setSuccessResult(null);
    setSelectedStrategyId("");

    getStrategies()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.data || [];
        setStrategies(list);
        if (list.length > 0) {
          setSelectedStrategyId(getStrategyId(list[0]));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getErrorMessage(err));
          setStrategies([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingStrategies(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    setSuccessResult(null);
    onClose();
  }, [isSubmitting, onClose]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (successResult) {
        handleClose();
        return;
      }

      if (!selectedStrategyId) {
        setError("Select a strategy.");
        return;
      }

      setIsSubmitting(true);
      setError("");

      try {
        const response = await createStrategyBatch(selectedStrategyId);
        const result = parseCreateBatchResponse(response);
        const payload = {
          ...result,
          strategyId: result.strategyId || selectedStrategyId,
        };
        onSuccess?.(payload);
        setSuccessResult(payload);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleClose, onSuccess, selectedStrategyId, successResult]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ className: styles["create-batch-dialog"] }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(15, 23, 42, 0.4)" },
        },
      }}
    >
      <form className={styles["create-batch-form"]} onSubmit={handleSubmit} noValidate>
        <div className={styles["create-batch-header"]}>
          <div className={styles["create-batch-header-icon"]}>
            <GlobalIcon iconName="StrategyProfilingIcon" className={styles["create-batch-icon"]} />
          </div>
          <div className={styles["create-batch-header-text"]}>
            <h2 className={styles["create-batch-title"]}>Create Batch ID</h2>
            <p className={styles["create-batch-subtitle"]}>
              {successResult
                ? "Your new batch ID has been created."
                : "Select a strategy to generate a new batch."}
            </p>
          </div>
          <IconButton
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className={styles["create-batch-close"]}
            aria-label="Close"
            size="small"
          >
            <GlobalIcon iconName="RoundedCrossIcon" className={styles["create-batch-close-icon"]} />
          </IconButton>
        </div>

        <div className={styles["create-batch-body"]}>
          {successResult ? (
            <Alert severity="success" className={styles["create-batch-success-alert"]}>
              <span className={styles["create-batch-success-message"]}>{successResult.message}</span>
              {successResult.description ? (
                <span className={styles["create-batch-success-desc"]}>{successResult.description}</span>
              ) : null}
            </Alert>
          ) : (
            <>
              <FormControl
                fullWidth
                size="small"
                disabled={isLoadingStrategies || isSubmitting || strategies.length === 0}
                className={styles["create-batch-field"]}
              >
                <InputLabel id="create-batch-strategy-label">Strategy</InputLabel>
                <Select
                  labelId="create-batch-strategy-label"
                  label="Strategy"
                  value={selectedStrategyId}
                  onChange={(event) => {
                    setSelectedStrategyId(event.target.value);
                    if (error) setError("");
                  }}
                  MenuProps={STRATEGY_SELECT_MENU_PROPS}
                >
                  {strategies.map((strategy) => {
                    const strategyId = getStrategyId(strategy);
                    return (
                      <MenuItem
                        key={strategyId}
                        value={strategyId}
                        className={styles["create-batch-menu-item"]}
                        title={strategy.strategy_name || "Unknown"}
                      >
                        {strategy.strategy_name || "Unknown"}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {isLoadingStrategies ? (
                <div className={styles["create-batch-loading"]}>Loading strategies...</div>
              ) : null}
            </>
          )}

          {error ? (
            <Alert severity="error" className={styles["create-batch-alert"]}>
              {error}
            </Alert>
          ) : null}
        </div>

        <div className={styles["create-batch-actions"]}>
          {successResult ? (
            <Button
              type="button"
              variant="contained"
              onClick={handleClose}
              className={styles["create-batch-submit"]}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className={styles["create-batch-cancel"]}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!selectedStrategyId || isLoadingStrategies || isSubmitting}
                className={styles["create-batch-submit"]}
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <GlobalIcon iconName="AddIcon" className={styles["create-batch-submit-icon"]} />
                  )
                }
              >
                {isSubmitting ? "Creating…" : "Create Batch ID"}
              </Button>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}

export default CreateBatchIdModal;
